namespace Shared.Dto

open System

type MapSensorStateDto =
    | Active
    | Disabled

type BaseMapSensorDto = {
    Name: string
    Location: Location
    Altitude: float option
    IsPublic: bool
    State: MapSensorStateDto
    PhysicalSensorId: int option
    UserId: int option
    Note: string option
    LastAssignmentDate: DateTime option
    StateSince: DateTimeOffset
}

type SoilMapSensorDto = {
    Base: BaseMapSensorDto
    WetLimit: float option
    DryLimit: float option
}

type MapSensorDto =
    | Air of BaseMapSensorDto
    | Soil of SoilMapSensorDto
    | FourTimesSoil of BaseMapSensorDto
    | RainFall of BaseMapSensorDto
    | LeafletMoisture of BaseMapSensorDto
    | PH of BaseMapSensorDto
    | WindAverage of BaseMapSensorDto
    | WeatherStation of BaseMapSensorDto
    | LiquidLevel of BaseMapSensorDto
    | SolarRadiation of BaseMapSensorDto
    | Distance of BaseMapSensorDto
    | Pressure of BaseMapSensorDto
    | WaterQuality of BaseMapSensorDto

[<RequireQualifiedAccess>]
module MapSensor =
    let getBaseData (sensor: MapSensorDto) =
        match sensor with
        | MapSensorDto.Air data
        | MapSensorDto.RainFall data
        | MapSensorDto.PH data
        | MapSensorDto.LeafletMoisture data
        | MapSensorDto.RainFall data
        | MapSensorDto.WindAverage data -> data
        | MapSensorDto.Soil data -> data.Base
        | MapSensorDto.WeatherStation data -> data
        | MapSensorDto.LiquidLevel data -> data
        | MapSensorDto.SolarRadiation data -> data
        | MapSensorDto.FourTimesSoil data -> data
        | MapSensorDto.Distance data -> data
        | MapSensorDto.Pressure data -> data
        | MapSensorDto.WaterQuality data -> data

    let toSensorType (sensor: MapSensorDto) =
        match sensor with
        | MapSensorDto.Air _ -> MapSensorType.Air
        | MapSensorDto.Soil _ -> MapSensorType.Soil
        | MapSensorDto.RainFall _ -> MapSensorType.RainFall
        | MapSensorDto.LeafletMoisture _ -> MapSensorType.LeafletMoisture
        | MapSensorDto.PH _ -> MapSensorType.PH
        | MapSensorDto.WindAverage _ -> MapSensorType.WindAverage
        | MapSensorDto.WeatherStation _ -> MapSensorType.WeatherStation
        | MapSensorDto.LiquidLevel _ -> MapSensorType.LiquidLevel
        | MapSensorDto.SolarRadiation _ -> MapSensorType.SolarRadiation
        | MapSensorDto.FourTimesSoil _ -> MapSensorType.FourTimesSoil
        | MapSensorDto.Distance _ -> MapSensorType.Distance
        | MapSensorDto.Pressure _ -> MapSensorType.Pressure
        | MapSensorDto.WaterQuality _ -> MapSensorType.WaterQuality

    let mapBaseData f (sensor: MapSensorDto) : MapSensorDto =
        let newBaseData = getBaseData sensor |> f

        match sensor with
        | Air _ -> Air newBaseData
        | RainFall _ -> RainFall newBaseData
        | PH _ -> PH newBaseData
        | LeafletMoisture _ -> LeafletMoisture newBaseData
        | WindAverage _ -> WindAverage newBaseData
        | Soil data ->
            Soil {
                Base = newBaseData
                WetLimit = data.WetLimit
                DryLimit = data.DryLimit
            }
        | WeatherStation _ -> WeatherStation newBaseData
        | LiquidLevel _ -> LiquidLevel newBaseData
        | SolarRadiation _ -> SolarRadiation newBaseData
        | FourTimesSoil _ -> FourTimesSoil newBaseData
        | Distance _ -> Distance newBaseData
        | Pressure _ -> Pressure newBaseData
        | WaterQuality _ -> WaterQuality newBaseData

    let isActive (state: MapSensorDto) : bool =
        match (getBaseData state).State with
        | Active -> true
        | _ -> false